import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, FolderOpen, Plus, Search, Upload, Download, Archive, RotateCcw,
  Tag, Filter, Loader2, ChevronRight, File, Image, Table, Presentation,
  Trash2, Eye, Brain, MessageSquare, X, FolderPlus, MoreVertical, Clock,
  CheckCircle2, AlertTriangle, Sparkles, Send, ArrowLeft, Edit3
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { getPermissions } from '../../utils/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getHeaders = () => {
  const token = localStorage.getItem('d3_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};
const getAuthOnly = () => {
  const token = localStorage.getItem('d3_token');
  return { Authorization: `Bearer ${token}` };
};

const FILE_ICONS = {
  pdf: { icon: FileText, color: 'text-red-400', bg: 'bg-red-500/10' },
  doc: { icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  docx: { icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  xls: { icon: Table, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  xlsx: { icon: Table, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  csv: { icon: Table, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  ppt: { icon: Presentation, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  pptx: { icon: Presentation, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  txt: { icon: File, color: 'text-zinc-400', bg: 'bg-zinc-500/10' },
  png: { icon: Image, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  jpg: { icon: Image, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  jpeg: { icon: Image, color: 'text-purple-400', bg: 'bg-purple-500/10' },
};

const getFileIcon = (ext) => FILE_ICONS[ext] || { icon: File, color: 'text-zinc-400', bg: 'bg-zinc-500/10' };

const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const AIStatusBadge = ({ status }) => {
  const configs = {
    completed: { label: 'AI Analyzed', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', icon: CheckCircle2 },
    processing: { label: 'Processing', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', icon: Loader2 },
    pending: { label: 'Pending', color: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20', icon: Clock },
    failed: { label: 'Failed', color: 'text-red-400 bg-red-500/10 border-red-500/20', icon: AlertTriangle },
    skipped: { label: 'Skipped', color: 'text-zinc-500 bg-zinc-500/10 border-zinc-500/20', icon: X },
  };
  const cfg = configs[status] || configs.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${cfg.color}`}>
      <Icon className={`w-3 h-3 ${status === 'processing' ? 'animate-spin' : ''}`} /> {cfg.label}
    </span>
  );
};

// ── Upload Modal ──────────────────────────────────────────
const UploadModal = ({ isOpen, onClose, folders, currentFolderId, onUploaded }) => {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState('General');
  const [tags, setTags] = useState('');
  const [folderId, setFolderId] = useState(currentFolderId || '');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      if (folderId) formData.append('folderId', folderId);
      formData.append('category', category);
      if (tags) formData.append('tags', tags);

      try {
        const res = await fetch(`${API_URL}/api/documents/upload`, {
          method: 'POST', headers: getAuthOnly(), body: formData,
        });
        if (res.ok) { uploaded++; }
        else { const err = await res.json().catch(() => ({})); toast.error(`${file.name}: ${err.detail || 'Failed'}`); }
      } catch { toast.error(`${file.name}: Upload error`); }
      setProgress(Math.round(((uploaded) / files.length) * 100));
    }
    toast.success(`${uploaded}/${files.length} files uploaded`);
    setUploading(false);
    setFiles([]);
    setProgress(0);
    onUploaded();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0d1221] border border-white/10 rounded-2xl w-full max-w-lg p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="upload-modal">
        <h2 className="text-lg font-semibold text-white mb-4">Upload Documents</h2>

        {/* Drop zone */}
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-500/30 transition-colors mb-4"
          data-testid="upload-dropzone"
        >
          <Upload className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
          <p className="text-sm text-zinc-400">Click to select files or drag & drop</p>
          <p className="text-[10px] text-zinc-600 mt-1">PDF, DOC, DOCX, XLS, XLSX, CSV, PPT, PPTX, TXT, PNG, JPG, JPEG</p>
          <input ref={fileRef} type="file" multiple className="hidden" onChange={e => setFiles(Array.from(e.target.files))}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.png,.jpg,.jpeg" data-testid="file-input" />
        </div>

        {files.length > 0 && (
          <div className="space-y-1 mb-4 max-h-32 overflow-y-auto">
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-zinc-400 bg-white/5 px-3 py-1.5 rounded-lg">
                <File className="w-3 h-3 shrink-0" />
                <span className="truncate flex-1">{f.name}</span>
                <span className="text-zinc-600">{formatSize(f.size)}</span>
                <button onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-zinc-600 hover:text-red-400">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-indigo-500/40"
              data-testid="upload-category"
            >
              {['General', 'Contracts', 'Proposals', 'Requirements', 'Design', 'Reports', 'Policies', 'Templates', 'Presentations', 'Financials', 'Other'].map(c => (
                <option key={c} value={c} className="bg-[#111827]">{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Folder</label>
            <select value={folderId} onChange={e => setFolderId(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-indigo-500/40"
              data-testid="upload-folder"
            >
              <option value="" className="bg-[#111827]">Root (No folder)</option>
              {folders.map(f => <option key={f.id} value={f.id} className="bg-[#111827]">{f.name}</option>)}
            </select>
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-xs text-zinc-400 mb-1.5">Tags (comma-separated)</label>
          <input value={tags} onChange={e => setTags(e.target.value)} placeholder="e.g. contract, legal, Q4"
            className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
            data-testid="upload-tags" />
        </div>

        {uploading && (
          <div className="mb-4">
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-[10px] text-zinc-500 mt-1">Uploading... {progress}%</p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-400" data-testid="cancel-upload-btn">Cancel</button>
          <button onClick={handleUpload} disabled={files.length === 0 || uploading}
            className="px-5 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-40 transition-colors flex items-center gap-2"
            data-testid="submit-upload-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload {files.length > 0 && `(${files.length})`}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ── Document Detail Panel ─────────────────────────────────
const DocumentDetail = ({ doc, onClose, onRefresh }) => {
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    if (!doc) return;
    fetch(`${API_URL}/api/documents/${doc.id}/versions`, { headers: getHeaders() })
      .then(r => r.ok ? r.json() : []).then(setVersions).catch(() => {});
  }, [doc]);

  if (!doc) return null;
  const fi = getFileIcon(doc.ext);
  const Icon = fi.icon;

  const handleArchive = async () => {
    const res = await fetch(`${API_URL}/api/documents/${doc.id}/archive`, { method: 'POST', headers: getHeaders() });
    if (res.ok) { toast.success('Document archived'); onRefresh(); onClose(); }
  };

  const handleDownload = () => {
    const token = localStorage.getItem('d3_token');
    window.open(`${API_URL}/api/documents/${doc.id}/download?token=${token}`, '_blank');
  };

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
      className="bg-[#121214] border border-white/10 rounded-xl p-5 space-y-4" data-testid="document-detail-panel">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${fi.bg}`}>
            <Icon className={`w-5 h-5 ${fi.color}`} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">{doc.name}</h3>
            <p className="text-[10px] text-zinc-500">{formatSize(doc.fileSize)} &middot; v{doc.version} &middot; {doc.ext?.toUpperCase()}</p>
          </div>
        </div>
        <button onClick={onClose} className="text-zinc-600 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button onClick={handleDownload} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-indigo-600/20 text-indigo-300 border border-indigo-500/30 rounded-lg hover:bg-indigo-600/30" data-testid="download-doc-btn">
          <Download className="w-3 h-3" /> Download
        </button>
        <button onClick={handleArchive} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-600/10 text-red-400 border border-red-500/20 rounded-lg hover:bg-red-600/20" data-testid="archive-doc-btn">
          <Archive className="w-3 h-3" /> Archive
        </button>
      </div>

      {/* Metadata */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-zinc-500 w-16">Category</span>
          <span className="text-xs text-zinc-300">{doc.category}</span>
        </div>
        {doc.tags?.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-zinc-500 w-16">Tags</span>
            <div className="flex gap-1 flex-wrap">
              {doc.tags.map(t => <span key={t} className="px-2 py-0.5 text-[10px] bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 rounded-full">{t}</span>)}
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-zinc-500 w-16">Uploaded</span>
          <span className="text-xs text-zinc-300">{doc.uploadedByName} &middot; {new Date(doc.createdAt).toLocaleDateString()}</span>
        </div>
      </div>

      {/* AI Insights */}
      <div className="pt-3 border-t border-white/5">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
          <span className="text-xs font-semibold text-white">AI Insights</span>
          <AIStatusBadge status={doc.aiProcessing} />
        </div>
        {doc.aiProcessing === 'completed' && (
          <div className="space-y-2">
            {doc.aiSummary && <div><p className="text-[10px] text-zinc-500 mb-0.5">Summary</p><p className="text-xs text-zinc-300">{doc.aiSummary}</p></div>}
            {doc.aiKeyTopics?.length > 0 && (
              <div><p className="text-[10px] text-zinc-500 mb-0.5">Key Topics</p>
                <div className="flex gap-1 flex-wrap">{doc.aiKeyTopics.map(t => <span key={t} className="px-2 py-0.5 text-[10px] bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 rounded-full">{t}</span>)}</div>
              </div>
            )}
            {doc.aiRisks?.length > 0 && (
              <div><p className="text-[10px] text-zinc-500 mb-0.5">Risks Identified</p>
                <ul className="text-xs text-amber-300 space-y-0.5">{doc.aiRisks.map((r, i) => <li key={i} className="flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {r}</li>)}</ul>
              </div>
            )}
            {doc.aiActionItems?.length > 0 && (
              <div><p className="text-[10px] text-zinc-500 mb-0.5">Action Items</p>
                <ul className="text-xs text-blue-300 space-y-0.5">{doc.aiActionItems.map((a, i) => <li key={i}>• {a}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Version History */}
      {versions.length > 1 && (
        <div className="pt-3 border-t border-white/5">
          <p className="text-xs font-semibold text-white mb-2">Version History</p>
          <div className="space-y-1">
            {versions.map(v => (
              <div key={v.id} className="flex items-center gap-3 text-[11px] text-zinc-400 bg-white/[0.02] px-3 py-2 rounded-lg">
                <span className="font-medium text-white">v{v.version}</span>
                <span className="flex-1 truncate">{v.uploadedBy}</span>
                <span>{new Date(v.createdAt).toLocaleDateString()}</span>
                <span className="text-zinc-600">{formatSize(v.fileSize)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// ── AI Ask Panel ──────────────────────────────────────────
const AIAskPanel = () => {
  const [query, setQuery] = useState('');
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAsk = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/documents/ai/ask`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ query }),
      });
      if (res.ok) setAnswer(await res.json());
      else toast.error('AI query failed');
    } catch { toast.error('Error'); }
    setLoading(false);
  };

  return (
    <div className="bg-[#121214] border border-white/10 rounded-xl p-5" data-testid="ai-ask-panel">
      <div className="flex items-center gap-2 mb-3">
        <Brain className="w-4 h-4 text-indigo-400" />
        <h3 className="text-sm font-semibold text-white">Ask AI About Documents</h3>
      </div>
      <div className="flex gap-2 mb-3">
        <input value={query} onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleAsk(); }}
          placeholder="e.g. Summarize our requirements document..."
          className="flex-1 px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
          data-testid="ai-ask-input"
        />
        <button onClick={handleAsk} disabled={!query.trim() || loading}
          className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl disabled:opacity-40 transition-colors"
          data-testid="ai-ask-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
      {answer && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
          <p className="text-sm text-zinc-300 whitespace-pre-wrap" data-testid="ai-ask-answer">{answer.answer}</p>
          {answer.sources?.length > 0 && (
            <div className="mt-3 pt-2 border-t border-white/5">
              <p className="text-[10px] text-zinc-500 mb-1">Sources</p>
              <div className="flex gap-2 flex-wrap">
                {answer.sources.map(s => (
                  <span key={s.id} className="px-2 py-0.5 text-[10px] bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 rounded-full">{s.name}</span>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

// ── Main Documents Screen ─────────────────────────────────
export default function DocumentsScreen() {
  const { user } = useAuth();
  const perms = getPermissions(user?.role);
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const h = getHeaders();
      const params = new URLSearchParams();
      if (currentFolderId) params.set('folderId', currentFolderId);
      if (filterCategory) params.set('category', filterCategory);
      if (showArchived) params.set('archived', 'true');

      const [docsRes, foldersRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/documents?${params}`, { headers: h }),
        fetch(`${API_URL}/api/documents/folders/tree`, { headers: h }),
        fetch(`${API_URL}/api/documents/stats`, { headers: h }),
      ]);
      if (docsRes.ok) setDocuments(await docsRes.json());
      if (foldersRes.ok) setFolders(await foldersRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch { /* silent */ }
    setLoading(false);
  }, [currentFolderId, filterCategory, showArchived]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = async () => {
    if (!search.trim()) { setSearchResults(null); return; }
    try {
      const res = await fetch(`${API_URL}/api/documents/search`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ query: search }),
      });
      if (res.ok) setSearchResults(await res.json());
    } catch { toast.error('Search failed'); }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/documents/folders`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ name: newFolderName, parentId: currentFolderId }),
      });
      if (res.ok) { toast.success('Folder created'); setShowCreateFolder(false); setNewFolderName(''); fetchData(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Error'); }
  };

  const navigateToFolder = (folder) => {
    setCurrentFolderId(folder.id);
    setFolderPath(prev => [...prev, folder]);
    setSearchResults(null);
  };

  const navigateUp = (targetIdx) => {
    if (targetIdx < 0) {
      setCurrentFolderId(null);
      setFolderPath([]);
    } else {
      setCurrentFolderId(folderPath[targetIdx].id);
      setFolderPath(prev => prev.slice(0, targetIdx + 1));
    }
    setSearchResults(null);
  };

  const handleRestore = async (docId) => {
    const res = await fetch(`${API_URL}/api/documents/${docId}/restore`, { method: 'POST', headers: getHeaders() });
    if (res.ok) { toast.success('Document restored'); fetchData(); }
  };

  const currentFolders = folders.filter(f => f.parentId === currentFolderId);
  const displayDocs = searchResults ? searchResults.results : documents;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-indigo-400 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="documents-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Documents</h1>
          <p className="text-sm text-zinc-500 mt-1">Enterprise document repository with AI knowledge extraction</p>
        </div>
        <div className="flex gap-2">
          {perms.canCreateProject && (
            <>
              <button onClick={() => setShowCreateFolder(true)}
                className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-zinc-300 border border-white/10 rounded-xl hover:bg-white/5 transition-colors"
                data-testid="create-folder-btn"
              >
                <FolderPlus className="w-4 h-4" /> New Folder
              </button>
              <button onClick={() => setShowUpload(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-xl transition-colors shadow-[0_0_20px_rgba(79,70,229,0.2)]"
                data-testid="upload-doc-btn"
              >
                <Upload className="w-4 h-4" /> Upload
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-[#121214] border border-white/10 rounded-xl p-4">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Total Documents</p>
            <p className="text-2xl font-bold text-white">{stats.totalDocuments}</p>
          </div>
          <div className="bg-[#121214] border border-white/10 rounded-xl p-4">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Folders</p>
            <p className="text-2xl font-bold text-white">{stats.totalFolders}</p>
          </div>
          <div className="bg-[#121214] border border-white/10 rounded-xl p-4">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Storage Used</p>
            <p className="text-2xl font-bold text-white">{formatSize(stats.totalSize)}</p>
          </div>
          <div className="bg-[#121214] border border-white/10 rounded-xl p-4">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">AI Analyzed</p>
            <p className="text-2xl font-bold text-white">{stats.aiCompleted}<span className="text-sm text-zinc-500">/{stats.totalDocuments}</span></p>
          </div>
        </div>
      )}

      {/* AI Ask */}
      <AIAskPanel />

      {/* Search & Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input value={search} onChange={e => { setSearch(e.target.value); if (!e.target.value) setSearchResults(null); }}
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
            placeholder="Search documents..."
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-white/5 border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/30"
            data-testid="search-docs-input"
          />
        </div>
        <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setSearchResults(null); }}
          className="px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-xl text-zinc-300 focus:outline-none"
          data-testid="filter-category"
        >
          <option value="" className="bg-[#111827]">All Categories</option>
          {['General', 'Contracts', 'Proposals', 'Requirements', 'Design', 'Reports', 'Policies', 'Templates', 'Presentations', 'Financials', 'Other'].map(c => (
            <option key={c} value={c} className="bg-[#111827]">{c}</option>
          ))}
        </select>
        <button onClick={() => { setShowArchived(!showArchived); setSearchResults(null); }}
          className={`px-3 py-2 text-xs rounded-lg border transition-colors ${showArchived ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' : 'border-white/10 text-zinc-500 hover:text-zinc-300'}`}
          data-testid="toggle-archived"
        >
          <Archive className="w-3 h-3 inline mr-1" /> {showArchived ? 'Showing Archived' : 'Archived'}
        </button>
      </div>

      {/* Breadcrumb */}
      {(currentFolderId || searchResults) && (
        <div className="flex items-center gap-1 text-xs text-zinc-500">
          <button onClick={() => navigateUp(-1)} className="hover:text-indigo-400 transition-colors" data-testid="breadcrumb-root">Documents</button>
          {folderPath.map((f, idx) => (
            <span key={f.id} className="flex items-center gap-1">
              <ChevronRight className="w-3 h-3" />
              <button onClick={() => navigateUp(idx)} className="hover:text-indigo-400 transition-colors">{f.name}</button>
            </span>
          ))}
          {searchResults && <span className="ml-2 text-indigo-400">Search: &ldquo;{search}&rdquo; ({searchResults.count} results)</span>}
        </div>
      )}

      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Folders */}
          {!searchResults && currentFolders.length > 0 && (
            <div className="mb-4">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Folders</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {currentFolders.map(f => (
                  <button key={f.id} onClick={() => navigateToFolder(f)}
                    className="flex items-center gap-3 p-3 bg-[#121214] border border-white/10 rounded-xl hover:border-indigo-500/30 transition-colors text-left"
                    data-testid={`folder-${f.id}`}
                  >
                    <FolderOpen className="w-5 h-5 text-amber-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{f.name}</p>
                      {f.description && <p className="text-[10px] text-zinc-600 truncate">{f.description}</p>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Documents */}
          <div>
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">
              {searchResults ? `${searchResults.count} Results` : `${displayDocs.length} Documents`}
            </p>
            {displayDocs.length > 0 ? (
              <div className="space-y-2">
                {displayDocs.map(doc => {
                  const fi = getFileIcon(doc.ext);
                  const Icon = fi.icon;
                  return (
                    <motion.div key={doc.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      onClick={() => setSelectedDoc(doc)}
                      className={`flex items-center gap-4 p-3 bg-[#121214] border rounded-xl cursor-pointer transition-colors ${
                        selectedDoc?.id === doc.id ? 'border-indigo-500/40' : 'border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`doc-${doc.id}`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${fi.bg}`}>
                        <Icon className={`w-4.5 h-4.5 ${fi.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-white truncate">{doc.name}</h4>
                        <div className="flex items-center gap-3 text-[10px] text-zinc-500 mt-0.5">
                          <span>{formatSize(doc.fileSize)}</span>
                          <span>{doc.category}</span>
                          <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {doc.tags?.slice(0, 2).map(t => (
                          <span key={t} className="px-2 py-0.5 text-[9px] bg-white/5 text-zinc-400 rounded-full">{t}</span>
                        ))}
                        <AIStatusBadge status={doc.aiProcessing} />
                        {showArchived && (
                          <button onClick={(e) => { e.stopPropagation(); handleRestore(doc.id); }}
                            className="text-[10px] px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded hover:bg-emerald-500/20"
                            data-testid={`restore-${doc.id}`}
                          >
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-zinc-600">
                <FileText className="w-12 h-12 mb-4 text-zinc-700" />
                <h3 className="text-lg font-medium text-white mb-1">
                  {showArchived ? 'No archived documents' : 'No documents yet'}
                </h3>
                <p className="text-sm text-zinc-500 mb-4">
                  {showArchived ? 'Archived documents will appear here' : 'Upload your first document to get started'}
                </p>
                {!showArchived && (
                  <button onClick={() => setShowUpload(true)}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-xl transition-colors"
                    data-testid="empty-upload-btn"
                  >
                    <Upload className="w-4 h-4 inline mr-1" /> Upload Document
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Detail Sidebar */}
        {selectedDoc && (
          <div className="w-[340px] shrink-0">
            <DocumentDetail doc={selectedDoc} onClose={() => setSelectedDoc(null)} onRefresh={fetchData} />
          </div>
        )}
      </div>

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowCreateFolder(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0d1221] border border-white/10 rounded-2xl w-full max-w-sm p-6 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="create-folder-modal"
          >
            <h2 className="text-base font-semibold text-white mb-4">Create Folder</h2>
            <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); }}
              placeholder="Folder name" autoFocus
              className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40 mb-4"
              data-testid="folder-name-input"
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowCreateFolder(false)} className="px-4 py-2 text-sm text-zinc-400">Cancel</button>
              <button onClick={handleCreateFolder} disabled={!newFolderName.trim()}
                className="px-5 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-40"
                data-testid="save-folder-btn"
              >
                Create
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        folders={folders}
        currentFolderId={currentFolderId}
        onUploaded={fetchData}
      />
    </div>
  );
}
